import React from 'react';
import ValidatorSummary, { IValidatorDecisionConfig } from 'components/ValidatorSummary';
import { AsyncTaskStatus } from 'constants/conceptLinkerValidatorStatus';
import AgentQAItemsModel from 'models/AgentQAItemsModel';
import { AgentQAItemStatus, IAgentQAGroupedItem } from 'types/agentQA';

interface IProps {
  itemsModel: AgentQAItemsModel;
  onFilterChange: (filter: { type: string; value: string } | null) => void;
  activeFilter: { type: string; value: string } | null;
  onClearFilter: () => void;
}

const AgentQASummary: React.FC<IProps> = ({
  itemsModel,
  onFilterChange,
  activeFilter,
  onClearFilter,
}) => {
  // Map test results to decisions (pass/fail are like validator decisions)
  const decisions: IValidatorDecisionConfig[] = [
    { key: 'pass', label: 'Pass', color: '#4caf50' },
    { key: 'fail', label: 'Fail', color: '#f44336' },
  ];

  // ValidatorSummary expects a single PENDING status value
  // We'll normalize AgentQA's PENDING (0) and PROCESSING (1) to a single status
  const NORMALIZED_PROCESSING = 999; // Use a unique value for processing items

  const statusConfig = {
    PENDING: NORMALIZED_PROCESSING, // Show items with this normalized status as processing
    NEEDS_USER_REVIEW: -1, // Not used in AgentQA (automated tests)
    DONE: AgentQAItemStatus.DONE, // 2
  };

  const asyncTaskStatusConfig = {
    FAILED: AsyncTaskStatus.FAILED,
    DEAD_LETTER: AsyncTaskStatus.DEAD_LETTER,
  };

  // Adapter for filter change to match new signature
  const handleFilterChange = (type: string, value: string) => {
    onFilterChange({ type, value });
  };

  // Flatten grouped items into individual runs for summary counting
  const items = ((itemsModel.gridData as IAgentQAGroupedItem[]) || []).flatMap((groupedItem) => {
    return groupedItem.runs.map((run) => {
      // Normalize status: both PENDING (0) and PROCESSING (1) become NORMALIZED_PROCESSING
      let normalizedStatus = run.status;
      if (run.status === AgentQAItemStatus.PENDING || run.status === AgentQAItemStatus.PROCESSING) {
        normalizedStatus = NORMALIZED_PROCESSING;
      }

      return {
        id: run.id,
        status: normalizedStatus,
        async_task_status: run.async_task_status,
        decision: run.test_result ?? undefined,
        ai_result: {
          decision: run.test_result ?? undefined,
        },
      };
    });
  });

  return (
    <ValidatorSummary
      items={items}
      decisions={decisions}
      statusConfig={statusConfig}
      asyncTaskStatus={asyncTaskStatusConfig}
      onFilterChange={handleFilterChange}
      activeFilter={activeFilter}
      onClearFilter={onClearFilter}
      enableChangeGrouping={false}
      customFeatures={{}}
      colors={{
        processing: '#9C27B0',
        review: '#FFA726',
        error: '#E53935',
      }}
    />
  );
};

export default AgentQASummary;
